import { z } from "zod";

/**
 * The picker behind "add a tool to this Role".
 *
 * ── WHY IT IS ROLE-SCOPED WHEN THE POPULATION IS ORG-SCOPED ─────────────────
 *
 * The tools an organisation could grant do not depend on the Role. `:id` is here
 * for two reasons and the first is not negotiable:
 *
 *  1. 🔴 `RolesController` declares `@Get(":id")`, which matches ANY single
 *     segment — so `/api/roles/grantable-external-tools` is a route that resolves
 *     to "the Role whose id is grantable-external-tools" whenever registration
 *     order favours that controller. Several controllers in this module carry a
 *     header about exactly this hazard, and the ones that could not live under a
 *     second segment were moved to their own top-level path instead. A
 *     two-segment path is immune, because `:id` matches exactly one.
 *  2. It lets the response carry `alreadyGranted` per row, so the dialog does not
 *     have to join two reads and cannot show a stale tick when a grant lands
 *     between them.
 */

/**
 * One row of the picker.
 *
 * ⚠️ `owned` AND `connected` ARE REPORTED SEPARATELY AND NEITHER IS A PROMISE
 * THE TOOL RUNS. An organisation can own a catalogue row it has never connected,
 * and — far more commonly — connect a shared-catalogue tool it will never own.
 * `Proximus Global` is the second case exactly: 0 owned, 1 connected. Folding
 * the pair into one boolean loses the state a customer most needs to see.
 *
 * `id` is an `AgentExternalTool.id`, which is what a grant stores and what the
 * system map keys a tool node on.
 */
export const GrantableExternalToolSchema = z.object({
  id: z.string(),
  name: z.string(),
  iconUrl: z.string().nullable(),
  owned: z.boolean(),
  connected: z.boolean(),
  /** True when THIS Role already holds a grant for it. */
  alreadyGranted: z.boolean()
});

export type GrantableExternalTool = z.infer<typeof GrantableExternalToolSchema>;

export const GrantableExternalToolsListResponseSchema = z.object({
  tools: z.array(GrantableExternalToolSchema)
});
